import React, { useEffect, useState } from 'react';
import { Button, Input, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import cs_analysis_fetch from '../../../utils/cs_analysis_fetch';
import { TASK_MODULES, enum_label } from './task_ticket_links';

const MODULE_OPTIONS = ['DESIGN', 'RENDER', 'PRESENTATION', 'CATALOGUE', 'PRICING', 'PRODUCTION', 'CLIENT_ORG'];
const LABEL = { fontSize: 11, color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 };
const DUE_PICKS = [{ label: '+3d', days: 3 }, { label: '+1wk', days: 7 }, { label: '+2wk', days: 14 }, { label: '+1mo', days: 30 }];
const iso_days_hence = (n) => {
    const d = new Date();
    d.setDate(d.getDate() + n);
    return d.toISOString().slice(0, 10);
};

/**
 * Standalone "New Task" modal — creates a rep-owned Task NOT tied to any finding
 * (POST /actionables with no findingId; the backend already supports this).
 * This is the manual, rep-controlled creation path (no auto-creation anywhere).
 * Owner defaults to the client's assigned CS rep via /health, editable.
 *
 * from_client_ticket (optional, the ticket row {id, ref, title, module}):
 * the task is raised FROM a client ticket — the save goes to
 * POST /actionables/from-client-ticket/:ticketId (which links the two and
 * returns the existing open task if one already links the ticket) with the
 * edited fields; module choices narrow to the six product modules that
 * endpoint accepts (a MES / OTHER ticket needs an explicit pick — the
 * endpoint 400s "module required" otherwise). Pair with
 * prefill = task_prefill_from_ticket(ticket).
 */
const TaskCreateModal = ({ open, store_id, on_close, on_created, prefill, reps = [], rep_role = 'rep', self_email = '', from_client_ticket = null }) => {
    const module_options = from_client_ticket ? TASK_MODULES : MODULE_OPTIONS;
    const [module, set_module] = useState('DESIGN');
    const [title, set_title] = useState('');
    const [description, set_description] = useState('');
    const [owner, set_owner] = useState('');
    const [due_date, set_due_date] = useState('');
    const [finding_id, set_finding_id] = useState(null);
    const [busy, set_busy] = useState(false);
    const [err, set_err] = useState(null);

    // Seed the form each time the modal opens. When the AI proposes a task
    // (chat → live modal, no staging), `prefill` carries its drafted fields so
    // the rep just reviews + hits Create; otherwise it opens blank for manual
    // creation.
    useEffect(() => {
        if (open) {
            const p = prefill || {};
            set_module(module_options.includes(p.module) ? p.module : module_options[0]);
            set_title(p.title || '');
            set_description(p.description || '');
            set_owner(p.owner || self_email || '');
            set_due_date(p.dueDate ? String(p.dueDate).slice(0, 10) : '');
            set_finding_id(p.findingId || null);
            set_err(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Default the owner to the creating rep herself (a rep can only create for
    // herself; a lead/admin may reassign via the dropdown). Applied when the
    // reps list resolves, without clobbering a prefill / hand-picked value.
    useEffect(() => {
        if (open && self_email) set_owner((curr) => curr || self_email);
    }, [open, self_email]);

    const submit = async () => {
        if (!description.trim()) { set_err('Description is required.'); return; }
        set_busy(true);
        set_err(null);
        try {
            const fields = {
                module,
                title: title.trim() || undefined,
                description: description.trim(),
                owner: owner.trim() || undefined,
                dueDate: due_date ? new Date(due_date).toISOString() : undefined,
            };
            const created = from_client_ticket
                ? await cs_analysis_fetch({
                    // Raised from a client ticket: the endpoint creates + links in one
                    // go (or hands back the open task already linked to the ticket).
                    path: `/api/v1/clients/${encodeURIComponent(store_id)}/actionables/from-client-ticket/${encodeURIComponent(from_client_ticket.id)}`,
                    method: 'POST',
                    body: fields,
                })
                : await cs_analysis_fetch({
                    path: `/api/v1/clients/${encodeURIComponent(store_id)}/actionables`,
                    method: 'POST',
                    body: {
                        ...fields,
                        // findingId set only when the AI proposed a task tied to a
                        // finding; blank for a standalone rep-created task.
                        findingId: finding_id || undefined,
                    },
                });
            on_created(created);
        } catch (e) {
            set_err(e.message || String(e));
        } finally {
            set_busy(false);
        }
    };

    return (
        <Modal isOpen={open} toggle={on_close} size='lg'>
            <ModalHeader toggle={on_close}>
                New Task
                {from_client_ticket ? (
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b', background: '#eef2f6', borderRadius: 6, padding: '2px 8px', marginLeft: 10, verticalAlign: 'middle' }}>
                        from CT-{from_client_ticket.ref}
                    </span>
                ) : null}
            </ModalHeader>
            <ModalBody>
                <div style={{ fontSize: 12, color: '#7f8c8d', marginBottom: 12 }}>
                    {from_client_ticket
                        ? 'This task will be linked to the client ticket — it completes when the ticket (and anything else linked) is done. Edit anything first.'
                        : prefill
                            ? 'Review this proposed task and click Create — edit anything first.'
                            : 'Create a task for this client — a standalone to-do you own.'}
                </div>
                <div style={{ marginBottom: 12 }}>
                    <div style={LABEL}>Module</div>
                    <Input type='select' value={module} onChange={(e) => set_module(e.target.value)} style={{ width: 'auto', minWidth: 180, fontSize: 13 }}>
                        {module_options.map((m) => <option key={m} value={m}>{m}</option>)}
                    </Input>
                    {from_client_ticket && !TASK_MODULES.includes(from_client_ticket.module) ? (
                        <div style={{ fontSize: 11, color: '#b9770e', marginTop: 3 }}>
                            The ticket's module is {enum_label(from_client_ticket.module) || 'unset'} — pick the product module this task belongs to.
                        </div>
                    ) : null}
                </div>
                <div style={{ marginBottom: 12 }}>
                    <div style={LABEL}>Title</div>
                    <Input value={title} onChange={(e) => set_title(e.target.value)} placeholder='Short heading (optional)' style={{ fontSize: 13 }} />
                </div>
                <div style={{ marginBottom: 12 }}>
                    <div style={LABEL}>Description <span style={{ color: '#c0392b' }}>*</span></div>
                    <Input type='textarea' rows={4} value={description} onChange={(e) => set_description(e.target.value)} placeholder='What needs to be done?' style={{ fontSize: 13, lineHeight: 1.5 }} />
                </div>
                <div style={{ marginBottom: 12 }}>
                    <div style={LABEL}>Owner</div>
                    <Input
                        type='select'
                        value={owner}
                        disabled={rep_role === 'rep'}
                        title={rep_role === 'rep' ? 'You can only create tasks for yourself' : 'Assign owner'}
                        onChange={(e) => set_owner(e.target.value)}
                        style={{ width: 'auto', minWidth: 220, fontSize: 13 }}
                    >
                        <option value=''>unassigned</option>
                        {owner && !reps.some((rp) => rp.email === owner) ? <option value={owner}>{owner}</option> : null}
                        {reps.map((rp) => <option key={rp.userId} value={rp.email}>{rp.name}</option>)}
                    </Input>
                    {rep_role === 'rep' ? (
                        <div style={{ fontSize: 11, color: '#8a94a6', marginTop: 3 }}>A CS rep creates tasks for herself; a lead/admin can assign to others.</div>
                    ) : null}
                </div>
                <div style={{ marginBottom: 12 }}>
                    <div style={LABEL}>Due date</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Input type='date' value={due_date} onChange={(e) => set_due_date(e.target.value)} style={{ width: 'auto', minWidth: 160 }} />
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {DUE_PICKS.map((q) => {
                                const v = iso_days_hence(q.days);
                                const active = due_date === v;
                                return <Button key={q.label} size='sm' outline={!active} color={active ? 'primary' : 'secondary'} onClick={() => set_due_date(v)} style={{ fontSize: 12, padding: '2px 8px' }}>{q.label}</Button>;
                            })}
                        </div>
                    </div>
                </div>
                {err ? <div style={{ color: '#c0392b', fontSize: 12, marginTop: 6 }}>{err}</div> : null}
            </ModalBody>
            <ModalFooter>
                <Button color='secondary' outline onClick={on_close}>Cancel</Button>
                <Button color='primary' disabled={busy || !description.trim()} onClick={submit}>{busy ? 'Creating…' : 'Create Task'}</Button>
            </ModalFooter>
        </Modal>
    );
};

export default TaskCreateModal;
